import React, { useState, useEffect, useRef } from "react";
import { Room, RoomEvent, Track } from "livekit-client";

const TheoryWEBRTCMonitoring = () => {
  const baseUrl = process.env.REACT_APP_API_URL;
  const [connected, setConnected] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leaveMessage, setLeaveMessage] = useState("");
  const [authToken, setAuthToken] = useState(null);
  const [roomName, setRoomName] = useState(null);

  const roomsRef = useRef([]);
  const globalParticipantMapRef = useRef(new Map());
  const participantIdentityMapRef = useRef(new Map());

  useEffect(() => {
    try {
      const persistedAuth = window.localStorage?.getItem("persist:tagnet-auth");
      if (persistedAuth) {
        const parsed = JSON.parse(persistedAuth);
        const userData = parsed.user ? JSON.parse(parsed.user) : null;
        const authToken = parsed.authToken
          ? JSON.parse(parsed.authToken)
          : null;
        setAuthToken(authToken);
        setUser(userData);
        if (authToken) {
          get_webrtc_token_fun(authToken);
        }
      }
    } catch (err) {
      console.error("Error parsing user data:", err);
      setLoading(false);
    }
  }, []);

  const get_webrtc_token_fun = async (token) => {
    try {
      const myHeaders = new Headers();
      myHeaders.append("Accept", "application/json");
      myHeaders.append("Authorization", `Bearer ${token}`);

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        redirect: "follow",
      };

      const response = await fetch(
        `${baseUrl}/api/get_theory_rtc_token`,
        requestOptions,
      );
      const result = await response.json();

      console.log("✅ API Response:", result);

      if (result.status && result.monitor_room && result.trainee_rooms) {
        await startMonitoring(result);
      }
    } catch (error) {
      console.error("❌ Error fetching token:", error);
      setLoading(false);
    }
  };

  const decodeIdentity = (identityStr) => {
    try {
      return JSON.parse(atob(identityStr));
    } catch (err) {
      return { username: identityStr };
    }
  };

  const updateParticipantsList = () => {
    const participantsArray = Array.from(
      globalParticipantMapRef.current.values(),
    );
    console.log("📊 Total unique participants:", participantsArray.length);
    setParticipants(participantsArray);
  };

  const createOrGetParticipant = (participant, roomName) => {
    const identity = decodeIdentity(participant.identity);
    const uniqueKey = identity.username || participant.identity;

    // 🚫 SKIP admin username
    if (uniqueKey.toLowerCase() === "admin") {
      console.log(`⏭️ Skipping admin user`);
      return null;
    }

    // 🚫 SKIP assessor role
    if (identity?.role?.toLowerCase() === "assessor") {
      console.log(`⏭️ Skipping assessor: ${uniqueKey}`);
      return null;
    }

    // Check if this user already exists (across all rooms)
    const existingParticipant = globalParticipantMapRef.current.get(uniqueKey);

    if (existingParticipant) {
      console.log(`✅ User ${uniqueKey} already exists, skipping duplicate`);
      return existingParticipant;
    }

    // Create new participant object
    const participantObj = {
      id: participant.sid,
      sid: participant.sid,
      identity: participant.identity,
      uniqueKey: uniqueKey,
      participant: participant,
      roomName: roomName,
      decodedIdentity: identity,
      isLocal: false,
    };

    // Add to maps
    globalParticipantMapRef.current.set(uniqueKey, participantObj);
    participantIdentityMapRef.current.set(
      participant.identity,
      participant.sid,
    );

    console.log(
      `➕ Added participant: ${identity.username} (${identity.role}) from room ${roomName}`,
    );

    return participantObj;
  };

  const removeParticipant = (participant) => {
    const identity = decodeIdentity(participant.identity);
    const uniqueKey = identity.username || participant.identity;

    // Skip admin
    if (uniqueKey.toLowerCase() === "admin") {
      return;
    }

    // Skip assessor
    if (identity?.role?.toLowerCase() === "assessor") {
      return;
    }

    globalParticipantMapRef.current.delete(uniqueKey);
    participantIdentityMapRef.current.delete(participant.identity);

    console.log(`➖ Removed participant: ${identity.username}`);

    setLeaveMessage(
      `${identity.name || identity.username || "Someone"} has left the room.`,
    );
    setTimeout(() => setLeaveMessage(""), 3000);

    updateParticipantsList();
  };

  const startMonitoring = async (data) => {
    try {
      const serverUrl = "https://webrtc.tagindia.co.in";
      const allRoomTokens = [data.monitor_room, ...data.trainee_rooms];

      console.log(`🔗 Connecting to ${allRoomTokens.length} rooms...`);

      // Suppress global console errors for DataChannel
      const originalError = console.error;
      console.error = (...args) => {
        const message = args[0]?.toString() || "";
        if (
          message.includes("DataChannel") ||
          message.includes("lossy") ||
          message.includes("reliable")
        ) {
          // Suppress DataChannel errors
          return;
        }
        originalError.apply(console, args);
      };

      let isFirstRoom = true;

      for (const roomInfo of allRoomTokens) {
        const { room: roomName, token } = roomInfo;
        setRoomName(roomName);

        const room = new Room({
          autoSubscribe: true,
          adaptiveStream: true,
          dynacast: true,
          reconnectPolicy: {
            maxAttempts: 5,
            initialDelay: 1000,
            backoffFactor: 1.5,
          },
        });

        // Handle room-level errors
        room.on(RoomEvent.Disconnected, () => {
          console.log(`🔌 Disconnected from room: ${roomName}`);
        });

        room.on(RoomEvent.Reconnecting, () => {
          console.log(`🔄 Reconnecting to room: ${roomName}`);
        });

        room.on(RoomEvent.Reconnected, () => {
          console.log(`✅ Reconnected to room: ${roomName}`);
        });

        room.on(RoomEvent.ConnectionStateChanged, (state) => {
          console.log(`🔗 Connection state changed for ${roomName}: ${state}`);
        });

        // Suppress DataChannel errors (they're usually non-critical)
        room.on(RoomEvent.DataChannelError || "dataChannelError", (error) => {
          // Silently log DataChannel errors as they're typically harmless
          console.debug(`DataChannel warning in ${roomName}:`, error);
        });

        await room.connect(serverUrl, token);
        roomsRef.current.push(room);

        console.log(`✅ Connected to room: ${roomName}`);

        // Handle local participant (assessor) from first room only
        if (isFirstRoom) {
          const localParticipant = room.localParticipant;

          try {
            console.log(`🎥 Enabling camera...`);
            await localParticipant.setCameraEnabled(true);
            console.log(`✅ Camera enabled`);
          } catch (err) {
            console.error(`❌ Camera error:`, err);
            alert(`Camera error: ${err.message}. Please allow camera access.`);
          }

          try {
            console.log(`🎤 Enabling microphone...`);
            await localParticipant.setMicrophoneEnabled(true);
            console.log(`✅ Microphone enabled`);
          } catch (err) {
            console.error(`❌ Microphone error:`, err);
          }

          // Wait for tracks to be published
          await new Promise((resolve) => setTimeout(resolve, 1500));

          console.log(`📊 Local tracks:`, {
            video: localParticipant.videoTrackPublications.size,
            audio: localParticipant.audioTrackPublications.size,
          });

          // Add local participant to display (skip assessor and admin)
          const identity = decodeIdentity(localParticipant.identity);
          const uniqueKey = identity.username || localParticipant.identity;

          // Skip if admin or assessor
          if (
            uniqueKey.toLowerCase() !== "admin" &&
            identity?.role?.toLowerCase() !== "assessor"
          ) {
            const participantObj = {
              id: localParticipant.sid,
              sid: localParticipant.sid,
              identity: localParticipant.identity,
              uniqueKey: uniqueKey,
              participant: localParticipant,
              roomName: roomName,
              decodedIdentity: identity,
              isLocal: true,
            };

            globalParticipantMapRef.current.set(uniqueKey, participantObj);
            participantIdentityMapRef.current.set(
              localParticipant.identity,
              localParticipant.sid,
            );

            console.log(
              `➕ Added LOCAL participant: ${identity.username} (${identity.role})`,
            );
          } else {
            console.log(`⏭️ Skipping LOCAL assessor: ${identity.username}`);
          }

          isFirstRoom = false;
        }

        // Handle existing remote participants
        for (const participant of room.remoteParticipants.values()) {
          const participantObj = createOrGetParticipant(participant, roomName);

          if (!participantObj) continue; // Skip if admin or assessor

          // Subscribe to track events
          participant.on(RoomEvent.TrackSubscribed, (track, publication) => {
            console.log(
              `📥 Track subscribed: ${track.kind} for ${participant.identity}`,
            );
            updateParticipantsList();
          });

          participant.on(RoomEvent.TrackUnsubscribed, (track, publication) => {
            console.log(
              `📤 Track unsubscribed: ${track.kind} for ${participant.identity}`,
            );
            updateParticipantsList();
          });

          participant.on(RoomEvent.TrackMuted, (publication) => {
            console.log(
              `🔇 Track muted: ${publication.kind} for ${participant.identity}`,
            );
            updateParticipantsList();
          });

          participant.on(RoomEvent.TrackUnmuted, (publication) => {
            console.log(
              `🔊 Track unmuted: ${publication.kind} for ${participant.identity}`,
            );
            updateParticipantsList();
          });
        }

        // New participant connected
        room.on(RoomEvent.ParticipantConnected, (participant) => {
          console.log(`✅ Participant connected: ${participant.identity}`);
          const participantObj = createOrGetParticipant(participant, roomName);

          if (!participantObj) return; // Skip if admin or assessor

          participant.on(RoomEvent.TrackSubscribed, (track, publication) => {
            console.log(
              `📥 Track subscribed: ${track.kind} for ${participant.identity}`,
            );
            updateParticipantsList();
          });

          participant.on(RoomEvent.TrackUnsubscribed, (track, publication) => {
            console.log(
              `📤 Track unsubscribed: ${track.kind} for ${participant.identity}`,
            );
            updateParticipantsList();
          });

          participant.on(RoomEvent.TrackMuted, (publication) => {
            console.log(
              `🔇 Track muted: ${publication.kind} for ${participant.identity}`,
            );
            updateParticipantsList();
          });

          participant.on(RoomEvent.TrackUnmuted, (publication) => {
            console.log(
              `🔊 Track unmuted: ${publication.kind} for ${participant.identity}`,
            );
            updateParticipantsList();
          });

          updateParticipantsList();
        });

        // Participant disconnected
        room.on(RoomEvent.ParticipantDisconnected, (participant) => {
          console.log(`❌ Participant disconnected: ${participant.identity}`);
          removeParticipant(participant);
        });

        updateParticipantsList();
      }

      setConnected(true);
      setLoading(false);
      console.log(`✅ Connected to ${allRoomTokens.length} rooms`);
    } catch (error) {
      console.error("❌ Failed to start monitoring:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      roomsRef.current.forEach((room) => {
        room.disconnect();
      });
    };
  }, []);

  if (loading) {
    return (
      <div className="container-fluid py-4 text-center">
        <h4>Loading monitoring interface...</h4>
        <div className="spinner-border text-primary mt-3" role="status">
          <span className="visually-hidden"></span>
        </div>
      </div>
    );
  }

  const sortedParticipants = [...participants].sort((a, b) => {
    const roleA = a.decodedIdentity?.role?.toLowerCase() || "";
    const roleB = b.decodedIdentity?.role?.toLowerCase() || "";

    if (roleA === "assessor" && roleB !== "assessor") return -1;
    if (roleA !== "assessor" && roleB === "assessor") return 1;
    return 0;
  });

  return (
    <div className="container-fluid py-4">
      <div className="text-center mb-4">
        <h4>Monitor Theory Exam for batch {user ? user.BatchId : "..."}</h4>
        {user && (
          <p className="text-muted">Monitoring as: {user.Name || "Monitor"}</p>
        )}
      </div>

      <div className="mb-3 d-flex align-items-center gap-3 flex-wrap">
        <span
          className={`badge ${connected ? "bg-success" : "bg-warning"}`}
          style={{ fontSize: "14px", padding: "8px 12px" }}
        >
          {connected ? "● Connected" : "○ Connecting..."}
        </span>
        <span
          className="badge bg-info"
          style={{ fontSize: "14px", padding: "8px 12px" }}
        >
          🏠 {roomsRef.current.length} Rooms
        </span>
        <span
          className="badge bg-primary"
          style={{ fontSize: "14px", padding: "8px 12px" }}
        >
          👥 {participants.length} Participant
          {participants.length !== 1 ? "s" : ""}
        </span>
        {leaveMessage && (
          <span
            className="badge bg-danger"
            style={{ fontSize: "14px", padding: "8px 12px" }}
          >
            {leaveMessage}
          </span>
        )}
      </div>

      {participants.length === 0 && connected && (
        <div className="alert alert-warning text-center">
          No participants available yet. Waiting for trainees to join...
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: "16px",
          marginTop: "20px",
        }}
      >
        {sortedParticipants.map((p) => (
          <ParticipantVideo
            key={p.uniqueKey}
            participantData={p}
            baseUrl={baseUrl}
            authToken={authToken}
            roomName={roomName}
          />
        ))}
      </div>
    </div>
  );
};

const ParticipantVideo = ({
  participantData,
  baseUrl,
  authToken,
  roomName,
}) => {
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [isMicMuted, setIsMicMuted] = useState(false);

  const [videoAttached, setVideoAttached] = useState(false);
  const [audioAttached, setAudioAttached] = useState(false);

  const participant = participantData.participant;
  const identity = participantData.decodedIdentity;
  const isLocal = participantData.isLocal;

  useEffect(() => {
    if (!participant) return;

    const videoElement = videoRef.current;
    const audioElement = audioRef.current;

    const attachTracks = async () => {
      // Attach video track
      const videoPublication = Array.from(
        participant.videoTrackPublications.values(),
      ).find((pub) => pub.track && pub.kind === Track.Kind.Video);

      if (videoPublication?.track && videoElement) {
        const existingVideo = videoElement.querySelector("video");
        if (existingVideo) {
          videoPublication.track.detach(existingVideo);
        }

        videoPublication.track.attach(videoElement);
        setVideoAttached(true);
        console.log(
          `✅ Video attached for ${identity.username} (${
            isLocal ? "LOCAL" : "REMOTE"
          })`,
          {
            muted: videoPublication.track.isMuted,
            trackSid: videoPublication.trackSid,
          },
        );
      } else {
        console.warn(`⚠️ No video track for ${identity.username}`, {
          isLocal,
          publicationCount: participant.videoTrackPublications.size,
        });
      }

      // Attach audio track (mute for local to avoid feedback)
      const audioPublication = Array.from(
        participant.audioTrackPublications.values(),
      ).find((pub) => pub.track && pub.kind === Track.Kind.Audio);

      if (audioPublication?.track && audioElement && !isLocal) {
        if (audioElement.srcObject) {
          audioPublication.track.detach(audioElement);
        }

        audioPublication.track.attach(audioElement);
        audioElement
          .play()
          .catch((err) => console.warn("Audio play blocked:", err));
        setAudioAttached(true);
        console.log(`🔊 Audio attached for ${identity.username}`);
      }
    };

    const detachTracks = () => {
      const videoPublication = Array.from(
        participant.videoTrackPublications.values(),
      ).find((pub) => pub.track && pub.kind === Track.Kind.Video);
      if (videoPublication?.track && videoElement) {
        videoPublication.track.detach(videoElement);
        setVideoAttached(false);
      }

      const audioPublication = Array.from(
        participant.audioTrackPublications.values(),
      ).find((pub) => pub.track && pub.kind === Track.Kind.Audio);
      if (audioPublication?.track && audioElement && !isLocal) {
        audioPublication.track.detach(audioElement);
        setAudioAttached(false);
      }
    };

    // Initial attach
    attachTracks();

    // Listen for track events
    participant.on(RoomEvent.TrackPublished, (publication) => {
      console.log(
        `📤 Track published for ${identity.username}: ${publication.kind}`,
      );
      setTimeout(attachTracks, 500); // Small delay for track to be ready
    });

    participant.on(RoomEvent.TrackSubscribed, (track, publication) => {
      console.log(
        `🎬 Track subscribed for ${identity.username}: ${track.kind}`,
      );
      attachTracks();
    });

    participant.on(RoomEvent.TrackUnsubscribed, detachTracks);
    participant.on(RoomEvent.TrackMuted, () => {
      console.log(`🔇 Track muted for ${identity.username}`);
    });
    participant.on(RoomEvent.TrackUnmuted, () => {
      console.log(`🔊 Track unmuted for ${identity.username}`);
      attachTracks(); // Re-attach when unmuted
    });

    return () => {
      participant.off(RoomEvent.TrackPublished, attachTracks);
      participant.off(RoomEvent.TrackSubscribed, attachTracks);
      participant.off(RoomEvent.TrackUnsubscribed, detachTracks);
      detachTracks();
    };
  }, [participant, identity.username, isLocal]);

  const isAssessor = identity?.role?.toLowerCase() === "assessor";

  const toggleMicrophone = async () => {
    console.log("participant", participant);
    const firstKey = participant.audioTrackPublications.keys().next().value;

    console.log("participant-audioTrackPublications", firstKey);

    if (isLocal) {
      const newState = !isMicMuted;
      await participant.setMicrophoneEnabled(!newState);
      setIsMicMuted(newState);

      await callMuteAPI(
        roomName,
        participant.participantInfo?.identity,
        firstKey,
        newState,
      );
    } else {
      const newState = !isMicMuted;
      setIsMicMuted(newState);
      await callMuteAPI(
        roomName,
        participant.participantInfo?.identity,
        firstKey,
        newState,
      );
    }
  };

  const callMuteAPI = async (roomName, participantSid, trackSid, mute) => {
    try {
      const myHeaders = new Headers();
      myHeaders.append("Accept", "application/json");
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("Authorization", `Bearer ${authToken}`);

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: JSON.stringify({
          roomName: roomName,
          participantSid: participantSid,
          trackSid: trackSid,
          mute: mute,
        }),
      };

      const response = await fetch(
        `${baseUrl}/api/muteUnmuteParticipant`,
        requestOptions,
      );
      const result = await response.json();
      console.log("Mute/Unmute API response:", result);
    } catch (error) {
      console.error("Error calling mute/unmute API:", error);
    }
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        paddingBottom: "56.25%",
        background: "#1a1a1a",
        borderRadius: "12px",
        overflow: "hidden",
        boxShadow: isAssessor
          ? "0 4px 20px rgba(34, 197, 94, 0.4)"
          : "0 4px 12px rgba(0,0,0,0.15)",
        border: isAssessor ? "2px solid #22c55e" : "none",
      }}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={isLocal}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
      <audio ref={audioRef} autoPlay />

      {/* Control Buttons */}
      <div
        style={{
          position: "absolute",
          top: "16px",
          right: "16px",
          display: "flex",
          gap: "8px",
        }}
      >
        <button
          onClick={toggleMicrophone}
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            border: "none",
            background: isMicMuted ? "#ef4444" : "rgba(255,255,255,0.2)",
            color: "#fff",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "18px",
            backdropFilter: "blur(10px)",
          }}
          title={isMicMuted ? "Unmute Microphone" : "Mute Microphone"}
        >
          {isMicMuted ? "🔇" : "🎤"}
        </button>
      </div>

      {!videoAttached && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "#fff",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "48px", marginBottom: "8px" }}>📹</div>
          <div style={{ fontSize: "14px", opacity: 0.8 }}>
            Waiting for video...
          </div>
        </div>
      )}

      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          background: "linear-gradient(to top, rgba(0,0,0,0.9), transparent)",
          color: "#fff",
          padding: "16px",
          fontSize: "14px",
          fontWeight: "500",
        }}
      >
        <div style={{ marginBottom: "4px" }}>
          <strong style={{ fontSize: "16px" }}>
            {identity?.name || identity?.username || "Unknown"}
          </strong>
        </div>
        <div style={{ fontSize: "12px", opacity: 0.9, marginBottom: "2px" }}>
          Username: {identity?.username || "N/A"}
        </div>
      </div>
    </div>
  );
};

export default TheoryWEBRTCMonitoring;
